import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search, Building2, CheckCircle, Clock } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { motion } from "framer-motion";

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: 'TRY',
    minimumFractionDigits: 2
  }).format(amount);
};

const taxTypes = {
  kdv: "KDV",
  gelir_vergisi: "Gelir Vergisi",
  kurumlar_vergisi: "Kurumlar Vergisi",
  damga_vergisi: "Damga Vergisi",
  muhtasar: "Muhtasar",
  gecici_vergi: "Geçici Vergi",
  diger: "Diğer"
};

const Taxes = () => {
  const [taxes, setTaxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [paidFilter, setPaidFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTax, setEditingTax] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    type: "kdv",
    amount: "",
    period: "",
    due_date: "",
    paid: false,
    paid_date: "",
    notes: ""
  });

  const fetchTaxes = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/taxes`, { withCredentials: true });
      setTaxes(response.data);
    } catch (error) {
      toast.error("Veriler yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTaxes();
  }, [fetchTaxes]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        amount: parseFloat(formData.amount)
      };
      
      if (editingTax) {
        await axios.put(`${API}/taxes/${editingTax.id}`, data, { withCredentials: true });
        toast.success("Vergi güncellendi");
      } else {
        await axios.post(`${API}/taxes`, data, { withCredentials: true });
        toast.success("Vergi eklendi");
      }
      setDialogOpen(false);
      resetForm();
      fetchTaxes();
    } catch (error) {
      toast.error("İşlem başarısız");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu vergi kaydını silmek istediğinizden emin misiniz?")) return;
    try {
      await axios.delete(`${API}/taxes/${id}`, { withCredentials: true });
      toast.success("Vergi silindi");
      fetchTaxes();
    } catch (error) {
      toast.error("Silme başarısız");
    }
  };

  const togglePaid = async (tax) => {
    try {
      const data = {
        ...tax,
        paid: !tax.paid,
        paid_date: !tax.paid ? new Date().toISOString().split('T')[0] : null
      };
      await axios.put(`${API}/taxes/${tax.id}`, data, { withCredentials: true });
      toast.success(data.paid ? "Vergi ödendi" : "Vergi beklemede");
      fetchTaxes();
    } catch (error) {
      toast.error("Güncelleme başarısız");
    }
  };

  const openEditDialog = (tax) => {
    setEditingTax(tax);
    setFormData({
      name: tax.name,
      type: tax.type,
      amount: tax.amount.toString(),
      period: tax.period,
      due_date: tax.due_date,
      paid: tax.paid,
      paid_date: tax.paid_date || "",
      notes: tax.notes || ""
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingTax(null);
    setFormData({
      name: "",
      type: "kdv",
      amount: "",
      period: "",
      due_date: "",
      paid: false,
      paid_date: "",
      notes: ""
    });
  };

  const filteredTaxes = taxes.filter(t => {
    const matchesSearch = t.name?.toLowerCase().includes(search.toLowerCase()) ||
      taxTypes[t.type]?.toLowerCase().includes(search.toLowerCase());
    const matchesPaid = paidFilter === "all" || 
      (paidFilter === "paid" && t.paid) || 
      (paidFilter === "unpaid" && !t.paid);
    return matchesSearch && matchesPaid;
  });

  const totalUnpaid = taxes.filter(t => !t.paid).reduce((sum, t) => sum + t.amount, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-amber-400 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="taxes-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-outfit font-semibold text-zinc-50">Vergi Takibi</h1>
          <p className="text-zinc-500 text-sm mt-1">
            Ödenmemiş Toplam: <span className="text-rose-400">{formatCurrency(totalUnpaid)}</span>
          </p>
        </div>
        <Button 
          onClick={() => { resetForm(); setDialogOpen(true); }}
          className="bg-amber-400 hover:bg-amber-300 text-zinc-950"
          data-testid="add-tax-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Yeni Vergi
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input 
            placeholder="Vergi ara..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-zinc-900 border-zinc-800"
            data-testid="tax-search"
          />
        </div>
        <Select value={paidFilter} onValueChange={setPaidFilter}>
          <SelectTrigger className="w-40 bg-zinc-900 border-zinc-800" data-testid="tax-paid-filter">
            <SelectValue placeholder="Durum" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            <SelectItem value="all">Tümü</SelectItem>
            <SelectItem value="paid">Ödenen</SelectItem>
            <SelectItem value="unpaid">Ödenmemiş</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-800">
                <th className="text-left px-4 py-3 text-xs font-medium text-zinc-500 uppercase tracking-wider">Vergi</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-zinc-500 uppercase tracking-wider">Tür</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-zinc-500 uppercase tracking-wider">Dönem</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-zinc-500 uppercase tracking-wider">Son Ödeme</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-zinc-500 uppercase tracking-wider">Tutar</th>
                <th className="text-center px-4 py-3 text-xs font-medium text-zinc-500 uppercase tracking-wider">Durum</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-zinc-500 uppercase tracking-wider">İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {filteredTaxes.map((tax, index) => (
                <motion.tr 
                  key={tax.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: index * 0.03 }}
                  onClick={() => openEditDialog(tax)}
                  className="border-b border-zinc-800 hover:bg-zinc-800/50 transition-colors cursor-pointer"
                  data-testid={`tax-row-${tax.id}`}
                >
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-zinc-800 rounded-sm flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-zinc-500" />
                      </div>
                      <div>
                        <p className="text-sm text-zinc-50">{tax.name}</p>
                        {tax.notes && (
                          <p className="text-xs text-zinc-500">{tax.notes}</p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4 text-sm text-zinc-400">
                    {taxTypes[tax.type] || tax.type}
                  </td>
                  <td className="px-4 py-4 text-sm text-zinc-400">
                    {tax.period}
                  </td>
                  <td className="px-4 py-4 text-sm text-zinc-400">
                    {tax.due_date}
                  </td>
                  <td className="px-4 py-4 text-right">
                    <span className="text-sm font-mono text-zinc-50">{formatCurrency(tax.amount)}</span>
                  </td>
                  <td className="px-4 py-4">
                    <button
                      onClick={(e) => { e.stopPropagation(); togglePaid(tax); }}
                      className={`mx-auto flex items-center gap-1 px-2 py-1 rounded-sm text-xs transition-colors ${
                        tax.paid 
                          ? 'bg-emerald-400/10 text-emerald-400 hover:bg-emerald-400/20' 
                          : 'bg-rose-400/10 text-rose-400 hover:bg-rose-400/20'
                      }`}
                      data-testid={`toggle-tax-paid-${tax.id}`}
                    >
                      {tax.paid ? <CheckCircle className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                      {tax.paid ? 'Ödendi' : 'Bekliyor'}
                    </button>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center justify-end gap-2">
                      <Pencil className="w-3 h-3 text-zinc-500" />
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={(e) => { e.stopPropagation(); handleDelete(tax.id); }}
                        className="hover:bg-rose-400/10 text-zinc-400 hover:text-rose-400"
                        data-testid={`delete-tax-${tax.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </motion.tr>
              ))}
              {filteredTaxes.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-zinc-500">
                    {search || paidFilter !== "all" ? "Sonuç bulunamadı" : "Henüz vergi kaydı eklenmemiş"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 font-outfit">
              {editingTax ? "Vergi Düzenle" : "Yeni Vergi"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label className="text-zinc-400">Vergi Adı *</Label>
                <Input 
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="Örn: 2024 Q1 KDV"
                  className="bg-zinc-950 border-zinc-800"
                  data-testid="tax-name-input"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Vergi Türü *</Label>
                <Select value={formData.type} onValueChange={(v) => setFormData({ ...formData, type: v })}>
                  <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="tax-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    {Object.entries(taxTypes).map(([key, label]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-zinc-400">Tutar (₺) *</Label>
                <Input 
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  required
                  className="bg-zinc-950 border-zinc-800"
                  data-testid="tax-amount-input"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Dönem *</Label>
                <Input 
                  value={formData.period}
                  onChange={(e) => setFormData({ ...formData, period: e.target.value })}
                  required
                  placeholder="Örn: 2024-Q1, 2024-01"
                  className="bg-zinc-950 border-zinc-800"
                  data-testid="tax-period-input"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Son Ödeme Tarihi *</Label>
                <Input 
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                  required
                  className="bg-zinc-950 border-zinc-800"
                  data-testid="tax-duedate-input"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Ödeme Tarihi</Label>
                <Input 
                  type="date"
                  value={formData.paid_date}
                  onChange={(e) => setFormData({ ...formData, paid_date: e.target.value, paid: !!e.target.value })}
                  className="bg-zinc-950 border-zinc-800"
                  data-testid="tax-paiddate-input"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-zinc-400">Notlar</Label>
                <Textarea 
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="bg-zinc-950 border-zinc-800"
                  rows={2}
                  data-testid="tax-notes-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setDialogOpen(false)}
                className="border-zinc-700 hover:bg-zinc-800"
              >
                İptal
              </Button>
              <Button 
                type="submit"
                className="bg-amber-400 hover:bg-amber-300 text-zinc-950"
                data-testid="save-tax-btn"
              >
                {editingTax ? "Güncelle" : "Kaydet"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Taxes;
